/**
 * ReasonChips \u2014 inline visual summary of why a risk score landed where it
 * did. Used in the Investigations events table and the new Top High-Risk
 * Events card on the Overview tab.
 *
 * The chips are intentionally short and plain-language. Hover (or click)
 * to reveal the full breakdown including:
 *   - All reason chips (top-N + the rest).
 *   - The per-category contribution to the final score (from the
 *     `categoryContributions` field plumbed by /api/events).
 *   - The raw codes for engineers.
 */
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { ParsedReason } from "@/lib/reasonCodes";
import { summariseReasons } from "@/lib/reasonCodes";

type Tone = "high" | "suspicious" | "watch" | "good" | "neutral";

const TONE_CLASS: Record<Tone, string> = {
  high:       "border-sev-high/40 bg-sev-high/10 text-sev-high",
  suspicious: "border-sev-suspicious/40 bg-sev-suspicious/10 text-sev-suspicious",
  watch:      "border-sev-watch/40 bg-sev-watch/10 text-sev-watch",
  good:       "border-sev-good/40 bg-sev-good/10 text-sev-good",
  neutral:    "border-border bg-surface-3 text-fg-muted",
};

interface Props {
  parsed: ParsedReason[];
  contributions?: Record<string, number>;
  /** Number of inline chips before the "+N more" pill. Default 2. */
  top?: number;
  /** When true, render only the strongest chip (used by tight tables). */
  compact?: boolean;
}

export function ReasonChips({ parsed, contributions, top = 2, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !document.getElementById("reason-popover")?.contains(target)) setOpen(false);
    }
    function esc(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    function updatePosition() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(420, window.innerWidth - 24);
      setPopoverPos({
        top: rect.bottom + 6,
        left: Math.max(12, Math.min(window.innerWidth - width - 12, rect.right - width)),
      });
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  if (!parsed.length) {
    return <span className="text-fg-subtle text-xs">\u2014</span>;
  }

  const { top: topChips, remaining } = summariseReasons(parsed, compact ? 1 : top);

  return (
    <div ref={ref} className="relative inline-flex max-w-full items-center gap-1">
      {topChips.map((r, i) => (
        <Chip key={i} tone={r.def.severity as Tone} title={r.def.plain}>
          {r.def.short}
        </Chip>
      ))}
      {(remaining > 0 || contributions) && (
        <button
          ref={triggerRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          className="rounded-md border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] text-fg-muted hover:bg-surface-3 hover:text-fg"
          aria-label="Show all reasons"
        >
          {remaining > 0 ? `+${remaining}` : "details"}
        </button>
      )}
      {open && popoverPos && createPortal(
        <ReasonPopover
          parsed={parsed}
          contributions={contributions}
          onClose={() => setOpen(false)}
          position={popoverPos}
        />,
        document.body,
      )}
    </div>
  );
}

function Chip({ tone, title, children }: { tone: Tone; title?: string; children: React.ReactNode }) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex max-w-[180px] truncate rounded-md border px-1.5 py-0.5 text-[11px] font-medium",
        TONE_CLASS[tone],
      )}
    >
      {children}
    </span>
  );
}

function ReasonPopover({ parsed, contributions, onClose, position }: {
  parsed: ParsedReason[];
  contributions?: Record<string, number>;
  onClose: () => void;
  position: { top: number; left: number };
}) {
  const sorted = [...parsed].sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight));
  const contribEntries = contributions
    ? Object.entries(contributions).filter(([, v]) => v !== 0).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    : [];

  return (
    <div
      id="reason-popover"
      role="dialog"
      onMouseDown={(e) => e.stopPropagation()}
      style={{ top: position.top, left: position.left }}
      className="fixed z-50 max-h-[80vh] w-[420px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-lg border border-border bg-surface-1 p-3 text-xs shadow-2xl"
    >
      <div className="flex items-start justify-between gap-2 pb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
          Why this scored
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-fg-subtle hover:text-fg"
          aria-label="Close"
        >\u00d7</button>
      </div>

      <div className="space-y-1.5">
        {sorted.map((r, i) => (
          <div key={i} className="flex items-start gap-2">
            <Chip tone={r.def.severity as Tone}>{r.def.short}</Chip>
            <div className="min-w-0 flex-1">
              <div className="text-fg">{r.def.plain}</div>
              <div className="mt-0.5 font-mono text-[10px] text-fg-subtle">
                {r.raw}
                {r.weight !== 0 && (
                  <span className={clsx(
                    "ml-2",
                    r.weight > 0 ? "text-sev-high" : "text-sev-good",
                  )}>
                    {r.weight > 0 ? "+" : ""}{r.weight}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {contribEntries.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
            Score contribution by category
          </div>
          <div className="space-y-1">
            {contribEntries.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-32 truncate text-fg-muted">{k.replace(/_/g, " ")}</span>
                <span className={clsx(
                  "font-mono",
                  v > 0 ? "text-sev-high" : "text-sev-good",
                )}>{v > 0 ? "+" : ""}{Math.round(v)}</span>
                <span className="ml-auto h-1.5 w-24 rounded-full bg-surface-3">
                  <span
                    className={clsx(
                      "block h-full rounded-full",
                      v > 0 ? "bg-sev-high/50" : "bg-sev-good/50",
                    )}
                    style={{ width: `${Math.min(100, Math.abs(v) * 1.2)}%` }}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
